import { configValidationSchema } from './configValidationSchema';
import * as Sentry from '@sentry/nextjs';
import * as z from 'zod';
import { Theme } from './types/enums/theme';

const getEnvFromWindow = (name: string) => window.__ENV?.[name];
const getEnvFromProcess = (name: string) => global?.process?.env[name] || process.env[name];

let missingVariables: string[] = [];

const env = (
    name: string | undefined,
    nextRuntimeValue: string | undefined,
    defaultValue: string,
    logIfMissing = true,
) => {
    let value;
    if (!name) {
        return nextRuntimeValue || defaultValue;
    }

    if (typeof window !== 'undefined' && window.__ENV) {
        value = getEnvFromWindow(name) || nextRuntimeValue;
    } else {
        value = getEnvFromProcess(name) || nextRuntimeValue;
    }

    if (value === undefined || value === null) {
        if (logIfMissing) {
            missingVariables.push(name);
        }
        value = defaultValue;
    }

    return value;
};

const DEFAULT_TIMEOUT = '3600';
const APP_ENV = env('NEXT_PUBLIC_APP_ENV', process.env.NEXT_PUBLIC_APP_ENV, 'production');
const BASE_PATH = env(
    'NEXT_PUBLIC_BASE_PATH',
    process.env.NEXT_PUBLIC_BASE_PATH,
    'https://localhost:3000',
);
const API_BASE_PATH = env(
    'NEXT_PUBLIC_API_BASE_PATH',
    process.env.NEXT_PUBLIC_API_BASE_PATH,
    BASE_PATH,
    false,
);
const API_BASE_ENDPOINT = '/api';
const LIVE_API_BASE_PATH = `${API_BASE_PATH}${API_BASE_ENDPOINT}`;
const YOTI_URL = env(
    'NEXT_PUBLIC_YOTI_URL',
    process.env.NEXT_PUBLIC_YOTI_URL,
    'https://api.yoti.com',
);

type ConfigType = z.infer<typeof configValidationSchema>;

export const config: ConfigType = {
    loggingMissedConfigVariables: env(
        'LOGGING_CONFIG_VARS',
        process.env.LOGGING_CONFIG_VARS,
        'true',
    ),
    appEnv: APP_ENV,
    nodeEnv: env('NODE_ENV', process.env.NODE_ENV, 'development'),
    defaultLocale: env(
        'NEXT_PUBLIC_DEFAULT_LOCALE',
        process.env.NEXT_PUBLIC_DEFAULT_LOCALE,
        'en-INT',
    ),
    locales: env('NEXT_PUBLIC_LOCALES', process.env.NEXT_PUBLIC_LOCALES, 'en-INT')?.split(','),
    localeCookieName: env(
        'NEXT_PUBLIC_LOCALE_COOKIE_NAME',
        process.env.NEXT_PUBLIC_LOCALE_COOKIE_NAME,
        'NEXT_LOCALE',
    ),
    theme: env('NEXT_PUBLIC_THEME', process.env.NEXT_PUBLIC_THEME, 'mpanels') as Theme,
    googleFontsUrl: env('NEXT_PUBLIC_GOOGLE_FONTS', process.env.NEXT_PUBLIC_GOOGLE_FONTS, ''),
    osanoCookieUrlToken: env(
        'NEXT_PUBLIC_OSANO_COOKIE_URL_TOKEN',
        process.env.NEXT_PUBLIC_OSANO_COOKIE_URL_TOKEN,
        '',
    ),
    placeId: Number(env('NEXT_PUBLIC_PLACE_ID', process.env.NEXT_PUBLIC_PLACE_ID, '2')),
    basePath: BASE_PATH,
    apiBasePath: API_BASE_PATH,
    filesBasePath: APP_ENV === 'local' ? API_BASE_PATH : BASE_PATH,
    routingForLinksInPhrasesEnabled: env(
        'NEXT_PUBLIC_ROUTING_FOR_LINKS_IN_PHRASES_ENABLED',
        process.env.NEXT_PUBLIC_ROUTING_FOR_LINKS_IN_PHRASES_ENABLED,
        'true',
    ),
    dateFormat: 'MMM dd, yyyy',
    dateRange: {
        christmas: env(
            'NEXT_PUBLIC_CHRISTMAS_DATE_RANGE',
            process.env.NEXT_PUBLIC_CHRISTMAS_DATE_RANGE,
            '15/12,07/01',
        ).split(','),
    },

    api: {
        url: {
            auth: `${LIVE_API_BASE_PATH}/auth`,
            interaction: `${LIVE_API_BASE_PATH}/interaction`,
            config: `${LIVE_API_BASE_PATH}/config`,
            account: `${LIVE_API_BASE_PATH}/v2/account`,
            translate: `${LIVE_API_BASE_PATH}/translate`,
            dictionary: `${LIVE_API_BASE_PATH}/dictionary`,
            dynamicProfiling: `${LIVE_API_BASE_PATH}/profile`,
            registration: `${LIVE_API_BASE_PATH}/registration`,
            affiliates: `${LIVE_API_BASE_PATH}/affiliates`,
            verification: `${LIVE_API_BASE_PATH}/verification`,
            wallet: `${LIVE_API_BASE_PATH}/wallet`,
            survey: `${LIVE_API_BASE_PATH}/surveys`,
            surveyEnd: `${LIVE_API_BASE_PATH}/survey-end`,
            contactPreferences: `${LIVE_API_BASE_PATH}/contact-preferences`,
            support: `${LIVE_API_BASE_PATH}/support`,
            ccpa: `${LIVE_API_BASE_PATH}/ccpa`,
            yotiSrc: APP_ENV !== 'production' ? `${YOTI_URL}/sandbox` : YOTI_URL,
            yotiApi: 'https://api.yoti.com',
            nmDashboard: `${LIVE_API_BASE_PATH}/nmdashboard`,
            expertNetwork: `${LIVE_API_BASE_PATH}/expert-network`,
        },
    },
    timeout: {
        configUpdate: Number(
            env(
                'NEXT_PUBLIC_CONFIG_UPDATE_TIMEOUT',
                process.env.NEXT_PUBLIC_CONFIG_UPDATE_TIMEOUT,
                DEFAULT_TIMEOUT,
            ),
        ),
        inputsDataUpdate: Number(
            env(
                'NEXT_PUBLIC_INPUTS_DATA_UPDATE_TIMEOUT',
                process.env.NEXT_PUBLIC_INPUTS_DATA_UPDATE_TIMEOUT,
                DEFAULT_TIMEOUT,
            ),
        ),
        revalidate: Number(
            env(
                'NEXT_PUBLIC_REVALIDATE_TIME',
                process.env.NEXT_PUBLIC_REVALIDATE_TIME,
                DEFAULT_TIMEOUT,
            ),
        ),
        shortRevalidate: Number(
            env(
                'NEXT_PUBLIC_SHORT_REVALIDATE_TIME',
                process.env.NEXT_PUBLIC_SHORT_REVALIDATE_TIME,
                '30',
            ),
        ),
        serverRequestsRevalidate: Number(
            env(
                'NEXT_PUBLIC_SERVER_REQUESTS_REVALIDATE_TIME',
                process.env.NEXT_PUBLIC_SERVER_REQUESTS_REVALIDATE_TIME,
                DEFAULT_TIMEOUT,
            ),
        ),
        redirectOnEmailValidated: Number(
            env(
                'NEXT_PUBLIC_EMAIL_VALIDATION_TIMEOUT',
                process.env.NEXT_PUBLIC_EMAIL_VALIDATION_TIMEOUT,
                DEFAULT_TIMEOUT,
            ),
        ),
        accountDataRevalidate: Number(
            env(
                'NEXT_PUBLIC_ACCOUNT_DATA_REVALIDATE_TIME_MS',
                process.env.NEXT_PUBLIC_ACCOUNT_DATA_REVALIDATE_TIME_MS,
                '60000',
            ),
        ),
        accountSummaryRevalidate: Number(
            env(
                'NEXT_PUBLIC_ACCOUNT_SUMMARY_REVALIDATE_TIME_MS',
                process.env.NEXT_PUBLIC_ACCOUNT_SUMMARY_REVALIDATE_TIME_MS,
                '600000',
            ),
        ),
        yotiIdentityRevalidate: Number(
            env(
                'NEXT_PUBLIC_YOTI_DATA_REVALIDATE_TIME_MS',
                process.env.NEXT_PUBLIC_YOTI_DATA_REVALIDATE_TIME_MS,
                '5000',
            ),
        ),
        yotiIdentityPendingRevalidate: Number(
            env(
                'NEXT_PUBLIC_YOTI_DATA_PENDING_REVALIDATE_TIME_MS',
                process.env.NEXT_PUBLIC_YOTI_DATA_PENDING_REVALIDATE_TIME_MS,
                '25000',
            ),
        ),
        totpTokenRevalidate: Number(
            env(
                'NEXT_PUBLIC_TOTP_TOKEN_REVALIDATE_TIME_MS',
                process.env.NEXT_PUBLIC_TOTP_TOKEN_REVALIDATE_TIME_MS,
                '360000',
            ),
        ),
    },
    featureFlags: {
        localeAutoSwitchingDisabled:
            env(
                'NEXT_PUBLIC_LOCALE_AUTOSWITCHING_DISABLED_FEATURE',
                process.env.NEXT_PUBLIC_LOCALE_AUTOSWITCHING_DISABLED_FEATURE,
                'false',
            ) === 'true',
        resetPassword:
            env(
                'NEXT_PUBLIC_RESET_PASSWORD_FEATURE',
                process.env.NEXT_PUBLIC_RESET_PASSWORD_FEATURE,
                'false',
            ) === 'true',
        // This feauture flag enabling mock data for new wallet methods
        newWalletMethods:
            env(
                'NEXT_PUBLIC_NEW_WALLET_METHODS_FEATURE',
                process.env.NEXT_PUBLIC_NEW_WALLET_METHODS_FEATURE,
                'false',
            ) === 'true',
        // This feature flag controls whether the personal tin field is displayed in the account invoicing form
        personalTinME1161:
            env(
                'NEXT_PUBLIC_PERSONAL_TIN_ME_1161_FEATURE',
                process.env.NEXT_PUBLIC_PERSONAL_TIN_ME_1161_FEATURE,
                'false',
            ) === 'true',
        // This feature flag controls whether the enroll page is available or not
        enrollPage:
            env(
                'NEXT_PUBLIC_ENROLL_PAGE_FEATURE',
                process.env.NEXT_PUBLIC_ENROLL_PAGE_FEATURE,
                'false',
            ) === 'true',
        // This feature flag controls whether to show info tooltip for phone field (client-side only)
        phoneInfoTooltip:
            env(
                'NEXT_PUBLIC_PHONE_INFO_TOOLTIP_FEATURE',
                process.env.NEXT_PUBLIC_PHONE_INFO_TOOLTIP_FEATURE,
                'false',
            ) === 'true',
        // This feature flag hardcodes the home page title until the phraseId can be updated
        m3grRedesign:
            env(
                'NEXT_PUBLIC_M3GR_REDESIGN_FEATURE',
                process.env.NEXT_PUBLIC_M3GR_REDESIGN_FEATURE,
                'false',
            ) === 'true',
        // This feature flag indicates whether to use the new logic for the medical number in the Registration Professional Details form
        newMedicalNumber:
            env(
                'NEXT_PUBLIC_NEW_MEDICAL_NUMBER_FEATURE',
                process.env.NEXT_PUBLIC_NEW_MEDICAL_NUMBER_FEATURE,
                'false',
            ) === 'true',
        // This feature flag controls whether the Select Opinion page is enabled
        selectOpinion:
            env(
                'NEXT_PUBLIC_SELECT_OPINION_FEATURE',
                process.env.NEXT_PUBLIC_SELECT_OPINION_FEATURE,
                'false',
            ) === 'true',
        // This feature flag controls whether the Custom Invitation checkbox is visible on contact preferences
        customInvitation:
            env(
                'NEXT_PUBLIC_CUSTOM_INVITATION_FEATURE',
                process.env.NEXT_PUBLIC_CUSTOM_INVITATION_FEATURE,
                'false',
            ) === 'true',
    },
};

const reportMissingVariables = () => {
    const filteredMissingVariables = missingVariables.filter(
        (name) => !(name in config.featureFlags),
    );
    if (filteredMissingVariables.length > 0) {
        Sentry.captureException(new Error(`Missing config variables, fallback values are used`), {
            extra: {
                missingVariables: filteredMissingVariables,
            },
        });
        missingVariables = []; // Reset the list after reporting
    }
};
reportMissingVariables();

export const isProd = () => config.appEnv === 'production';
export const isFeatureEnabled = (name: keyof typeof config.featureFlags) =>
    config.featureFlags[name];

export const isTheme = (theme: Theme) => config.theme === theme;
